// SSO email discovery for /sso-login. Moved out of an inline script in
// sso-login.hbs so the control page and the login-experiment pages
// (z94kydcrz3) run the same code and the same error messages.
import { trackAuth } from "./services/authEvents";

const ERROR_MESSAGES = {
  "not-configured":
    "SSO is not configured or enabled for this email domain. Please use email/password login or contact your admin.",
  "missing-domain": "Please enter your work email to continue with SSO.",
  "missing-saml-response":
    "The identity provider did not return a SAML response. Please try again.",
  "invalid-domain":
    "Your identity provider returned an email from a domain that is not enabled for this SSO configuration.",
  "invalid-issuer":
    "The identity provider response does not match the configured Entity ID.",
  "invalid-audience":
    "The identity provider response was not issued for this Social Champ SSO application.",
  "invalid-recipient":
    "The identity provider response was sent to an unexpected reply URL.",
  "expired-response":
    "The identity provider response has expired. Please try again.",
  "invalid-response":
    "The identity provider response could not be matched to this login request. Please try again.",
  "invalid-session": "Your SSO login session expired. Please start again.",
  "invalid-saml-response":
    "The identity provider returned an invalid SAML response.",
  "invalid-invite":
    "SSO succeeded, but this invite could not be accepted. Please ask your admin to send a new invitation.",
  "user-not-found":
    "SSO succeeded, but no Social Champ user exists for this email address. Please ask an admin to invite or create this user.",
  "public-domain":
    "Please use your company email address. Personal domains (gmail.com, etc.) can't be used for SSO.",
  "access-denied":
    "Your company SSO login was successful, but you don't have access to this Social Champ account. Please contact your admin.",
};

export default function ssoLogin() {
  const form = document.getElementById("form-sso-login");
  if (!form) return;
  const button = document.getElementById("submit-sso-login");
  const alert = document.getElementById("sso-alert");
  const alertText = alert ? alert.querySelector(".text") : null;
  const params = new URLSearchParams(window.location.search);

  function showError(message) {
    if (!alert || !alertText) return;
    alertText.innerText = message;
    alert.classList.remove("d-none");
    alert.classList.add("d-block");
  }

  function hideError() {
    if (!alert || !alertText) return;
    alertText.innerText = "";
    alert.classList.add("d-none");
    alert.classList.remove("d-block");
  }

  function setLoading(isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle("active", isLoading);
  }

  const error = params.get("error");
  // Own keys only: ?error=constructor must not read Object.prototype.
  if (error && Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, error)) {
    showError(ERROR_MESSAGES[error]);
    // Coming back from the IdP with an error is a failed SSO login.
    trackAuth("login_failure", {
      method: "sso",
      failure_reason: `sso_${error.replace(/-/g, "_")}`,
    });
  }

  const emailField = form.querySelector("input[name=email]");
  const emailParam = params.get("email");
  if (emailField && emailParam) {
    emailField.value = emailParam;
  }

  form.addEventListener("submit", function (event) {
    event.preventDefault();
    hideError();

    const email = emailField && emailField.value ? emailField.value.trim() : "";
    if (!email || email.indexOf("@") === -1) {
      showError("Please enter a valid work email address.");
      return;
    }

    setLoading(true);
    fetch("/auth/sso/saml/discover", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: email,
        redirectUrl: params.get("redirectUrl") || "",
        inviteToken: params.get("inviteToken") || "",
      }),
    })
      .then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok || !body.status) {
            throw new Error(body.message || "Unable to start SSO login.");
          }
          return body;
        });
      })
      .then(function (body) {
        // A funnel step, not a method choice: the click that chose SSO was
        // already counted as auth_method_click on the page that linked here.
        trackAuth("sso_redirect", { method: "sso" });
        window.location.href = body.data.redirectUrl;
      })
      .catch(function (err) {
        setLoading(false);
        trackAuth("login_failure", {
          method: "sso",
          failure_reason: "sso_discover_failed",
        });
        showError(err.message || "Unable to start SSO login.");
      });
  });
}
